import { createTheme } from '@mui/material/styles';

// Lino brand colors
const PRIMARY_COLOR = '#01178E'; // Deep blue
const ACCENT_COLOR = '#FFC200'; // Gold/yellow

export const theme = createTheme({
  palette: {
    primary: {
      main: PRIMARY_COLOR,
      light: '#3345B0',
      dark: '#000B5C',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: ACCENT_COLOR,
      light: '#FFD54F',
      dark: '#C79100',
      contrastText: '#000000',
    },
    background: {
      default: '#F5F5F5',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#212121',
      secondary: '#757575',
    },
    error: {
      main: '#D32F2F',
    },
    warning: {
      main: '#F57C00',
    },
    info: {
      main: '#0288D1',
    },
    success: {
      main: '#388E3C',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // Global focus styles for accessibility
        '*:focus-visible': {
          outline: `2px solid ${PRIMARY_COLOR}`,
          outlineOffset: '2px',
        },
        // Remove default focus outline for mouse users
        '*:focus:not(:focus-visible)': {
          outline: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          minHeight: 44, // Touch-friendly minimum height
          padding: '10px 20px',
          '@media (max-width: 600px)': {
            minHeight: 48, // Larger on mobile for better touch targets
            padding: '12px 24px',
          },
        },
        sizeSmall: {
          minHeight: 36,
          padding: '6px 16px',
          '@media (max-width: 600px)': {
            minHeight: 40,
            padding: '8px 16px',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          minWidth: 44, // Touch-friendly minimum size
          minHeight: 44,
          '@media (max-width: 600px)': {
            minWidth: 48,
            minHeight: 48,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
          '& .MuiInputBase-input': {
            minHeight: 44, // Touch-friendly input height
            '@media (max-width: 600px)': {
              minHeight: 48,
              fontSize: '16px', // Prevents zoom on iOS
            },
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          minHeight: 44,
          '@media (max-width: 600px)': {
            minHeight: 48,
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          minHeight: 44,
          '@media (max-width: 600px)': {
            minHeight: 48,
          },
        },
      },
    },
  },
});

// Function to create custom theme with workspace branding
export const createCustomTheme = (brandColor?: string, accentColor?: string) => {
  return createTheme({
    ...theme,
    palette: {
      ...theme.palette,
      primary: {
        main: brandColor || PRIMARY_COLOR,
        light: brandColor ? `${brandColor}33` : '#3345B0',
        dark: brandColor ? `${brandColor}CC` : '#000B5C',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: accentColor || ACCENT_COLOR,
        light: accentColor ? `${accentColor}33` : '#FFD54F',
        dark: accentColor ? `${accentColor}CC` : '#C79100',
        contrastText: '#000000',
      },
    },
  });
};
